import { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Brain,
  ChevronDown,
  CircleHelp,
  Crown,
  Flag,
  Flame,
  LayoutDashboard,
  Library,
  LibraryBig,
  LogOut,
  Menu,
  MessageSquare,
  NotebookPen,
  Scissors,
  Settings,
  Sparkles,
  TrendingUp,
  Trophy,
  Upload,
} from 'lucide-react';
import { useMaterial3Theme } from '../../contexts/Material3ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import analyticsService from '../../services/analytics';
import { getRouteCompatibility } from '../../config/routeCompatibility';
import './PremiumNavigation.css';

const SECTION_STORAGE_KEY = 'shelfquest_nav_sections';

function loadSectionState() {
  try {
    const saved = localStorage.getItem(SECTION_STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch { /* ignore */ }
  // Default: AI & Discovery open, Progress and System collapsed
  return { ai: true, progress: false, system: false };
}

function saveSectionState(state) {
  try {
    localStorage.setItem(SECTION_STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}

const PremiumNavigation = ({
  unreadNotesCount = 0,
  pendingUploads = 0,
  defaultCollapsed = false,
  isCollapsed,
  onCollapseChange,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { actualTheme } = useMaterial3Theme();
  const { logout } = useAuth();
  const { isPremium } = useEntitlements();
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const [sectionOpen, setSectionOpen] = useState(loadSectionState);

  const isControlled = typeof isCollapsed === 'boolean';
  const collapsed = isControlled ? isCollapsed : internalCollapsed;

  useEffect(() => {
    if (isControlled) {
      setInternalCollapsed(isCollapsed);
    }
  }, [isControlled, isCollapsed]);

  const handleCollapseToggle = () => {
    const newCollapsed = !collapsed;

    if (!isControlled) {
      setInternalCollapsed(newCollapsed);
    }

    onCollapseChange?.(newCollapsed);
  };

  const toggleSection = useCallback((key) => {
    setSectionOpen(prev => {
      const next = { ...prev, [key]: !prev[key] };
      saveSectionState(next);
      return next;
    });
  }, []);

  // Auto-expand section if user navigates to a page within it
  useEffect(() => {
    const aiPaths = ['/recommendations', '/mentor'];
    const progressPaths = ['/progress', '/achievements', '/goals', '/leaderboard', '/gamification'];
    const systemPaths = ['/settings', '/help', '/feedback'];

    setSectionOpen(prev => {
      let changed = false;
      const next = { ...prev };
      if (aiPaths.some(p => pathname === p || pathname.startsWith(p + '/')) && !prev.ai) {
        next.ai = true; changed = true;
      }
      if (progressPaths.some(p => pathname === p || pathname.startsWith(p + '/')) && !prev.progress) {
        next.progress = true; changed = true;
      }
      if (systemPaths.some(p => pathname === p || pathname.startsWith(p + '/')) && !prev.system) {
        next.system = true; changed = true;
      }
      if (changed) {
        saveSectionState(next);
        return next;
      }
      return prev;
    });
  }, [pathname]);

  // Main items — always visible
  const mainItems = [
    { to: '/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
    { to: '/library', label: 'Library', Icon: LibraryBig },
    { to: '/upload', label: 'Upload Books', Icon: Upload, badge: pendingUploads },
    { to: '/notes', label: 'Notes', Icon: NotebookPen, badge: unreadNotesCount },
    { to: '/clippings', label: 'Clippings', Icon: Scissors },
    { to: '/collections', label: 'Collections', Icon: Library },
  ];

  const sections = [
    {
      key: 'ai',
      label: 'AI & Discovery',
      Icon: Sparkles,
      items: [
        { to: '/recommendations', label: 'Recommendations', Icon: Sparkles },
        { to: '/mentor', label: 'Mentor AI', Icon: Brain },
        ...(!isPremium ? [{ to: '/pricing', label: 'Upgrade to Pro', Icon: Crown, className: 'nav-upgrade-accent' }] : []),
      ],
    },
    {
      key: 'progress',
      label: 'Progress',
      Icon: TrendingUp,
      items: [
        { to: '/streak', label: 'Streak', Icon: Flame },
        { to: '/progress', label: 'Progress & Journey', Icon: TrendingUp },
        { to: '/goals', label: 'Goals & Achievements', Icon: Flag },
        { to: '/leaderboard', label: 'Leaderboard', Icon: Trophy },
      ],
    },
    {
      key: 'system',
      label: 'System',
      Icon: Settings,
      items: [
        { to: '/settings', label: 'Settings', Icon: Settings },
        { to: '/help', label: 'Help & FAQ', Icon: CircleHelp },
        { to: '/feedback', label: 'Send Feedback', Icon: MessageSquare },
      ],
    },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const isActive = (to) => pathname === to || pathname.startsWith(to + '/');

  const trackNavigation = (to) => {
    const compatibility = getRouteCompatibility(to);
    analyticsService.trackEvent('navigation_selected', {
      category: 'core_funnel',
      source: collapsed ? 'desktop_rail_collapsed' : 'desktop_rail',
      destination: compatibility?.destination || 'utility',
      route_template: compatibility?.current || to
    });
  };

  const renderNavItem = ({ to, label, Icon, badge, className: extraClass }) => (
    <NavLink
      key={to}
      to={to}
      onClick={() => trackNavigation(to)}
      className={`md3-rail-destination ${isActive(to) ? 'active' : ''} ${extraClass || ''}`}
    >
      <div className="md3-rail-destination-icon">
        <Icon size={22} strokeWidth={2} aria-hidden="true" />
        {badge > 0 && (
          <span className="md3-badge">{badge > 99 ? '99+' : badge}</span>
        )}
      </div>
      {!collapsed && (
        <span className="md3-rail-destination-label">{label}</span>
      )}
    </NavLink>
  );

  const sectionHasActive = (items) => items.some(item => isActive(item.to));

  return (
    <nav
      className={`md3-navigation-rail ${collapsed ? 'collapsed' : 'expanded'} ${actualTheme === 'dark' ? 'dark' : ''}`}
      data-collapsed={collapsed}
      aria-label="Primary"
    >
      <div className="md3-rail-header">
        <button
          type="button"
          className="md3-icon-button"
          onClick={handleCollapseToggle}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          <Menu size={24} aria-hidden="true" />
        </button>
        {!collapsed && (
          <span className="md3-rail-title">ShelfQuest</span>
        )}
      </div>

      <div className="md3-rail-destinations">
        {/* Main items — always visible */}
        {mainItems.map(renderNavItem)}

        {/* Collapsible sections */}
        {sections.map(section => {
          const isOpen = sectionOpen[section.key];
          const hasActive = sectionHasActive(section.items);

          if (collapsed) {
            // In collapsed mode, show section icon as a single button
            return (
              <button
                type="button"
                key={section.key}
                className={`md3-rail-destination md3-rail-section-toggle ${hasActive ? 'active' : ''}`}
                onClick={() => toggleSection(section.key)}
                aria-label={`${sectionOpen[section.key] ? 'Collapse' : 'Expand'} ${section.label}`}
                aria-expanded={sectionOpen[section.key]}
              >
                <div className="md3-rail-destination-icon">
                  <section.Icon size={22} aria-hidden="true" />
                </div>
              </button>
            );
          }

          return (
            <div key={section.key} className="md3-rail-section" data-section={section.key}>
              <button
                type="button"
                className={`md3-rail-section-header ${hasActive ? 'has-active' : ''}`}
                onClick={() => toggleSection(section.key)}
                aria-expanded={isOpen}
              >
                <section.Icon className="md3-rail-section-icon" size={20} aria-hidden="true" />
                <span className="md3-rail-section-label">{section.label}</span>
                <ChevronDown className={`md3-rail-section-chevron ${isOpen ? 'open' : ''}`} size={18} aria-hidden="true" />
              </button>
              {isOpen && (
                <div className="md3-rail-section-items">
                  {section.items.map(renderNavItem)}
                </div>
              )}
            </div>
          );
        })}

        {/* Logout — always at bottom */}
        <button
          type="button"
          key="logout"
          className="md3-rail-destination md3-rail-destination-logout"
          onClick={handleLogout}
        >
          <div className="md3-rail-destination-icon">
            <LogOut size={22} aria-hidden="true" />
          </div>
          {!collapsed && (
            <span className="md3-rail-destination-label">Logout</span>
          )}
        </button>
      </div>
    </nav>
  );
};

export default PremiumNavigation;
